/**
 * Created: 2026-10-05
 * Initial commit: feat(invoice): add 发票管理 page listing site invoice requests
 *
 * 发票管理 page: a paginated grid of the current site's invoice requests,
 * newest first, with their basic information and status.
 */

import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import { Alert, Box, Chip, MenuItem, TextField, Typography } from '@mui/material'
import { DataGridPro, type GridColDef, type GridRowParams } from '@mui/x-data-grid-pro'
import { INVOICE_STATUS_CHIP, INVOICEE_TYPE_LABEL } from '@tc-app/shared/labels'
import { useCallback, useEffect, useRef, useState } from 'react'
import InvoiceRequestDialog from '../../components/InvoiceRequestDialog'
import UserAvatar from '../../components/UserAvatar'
import {
  type InvoiceeType,
  InvoiceRequestsDocument,
  type InvoiceRequestsQuery,
  InvoiceStatus,
} from '../../gql/graphql'
import { useRole } from '../../hooks'

gql`
  query InvoiceRequests(
    $filter: InvoiceRequestFilterInput!
    $first: Int
    $after: String
  ) {
    invoiceRequests(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          id
          status
          amount
          invoiceeType
          title
          taxNumber
          notifyEmail
          issuedAt
          createTime
          user {
            name
            avatar
            gender
            openid
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

type InvoiceRequestRow =
  InvoiceRequestsQuery['invoiceRequests']['edges'][number]['node']

const formatTime = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleString('zh-CN') : '-'

const columns: GridColDef<InvoiceRequestRow>[] = [
  {
    field: '__rowNumber',
    headerName: '#',
    width: 50,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
  },
  {
    field: 'user',
    headerName: '申请人',
    width: 180,
    sortable: false,
    headerAlign: 'center',
    renderCell: ({ row }) => <UserAvatar user={row.user} showMobile={false} />,
  },
  {
    field: 'invoiceeType',
    headerName: '抬头类型',
    width: 90,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    valueGetter: (value: InvoiceeType) => INVOICEE_TYPE_LABEL[value] ?? value,
  },
  {
    field: 'title',
    headerName: '发票抬头',
    flex: 1,
    minWidth: 180,
    sortable: false,
    headerAlign: 'center',
  },
  {
    field: 'taxNumber',
    headerName: '税号',
    width: 200,
    sortable: false,
    headerAlign: 'center',
    valueGetter: (value: string | null) => value || '-',
  },
  {
    field: 'notifyEmail',
    headerName: '接收邮箱',
    width: 200,
    sortable: false,
    headerAlign: 'center',
    valueGetter: (value: string | null) => value || '-',
  },
  {
    field: 'amount',
    headerName: '金额',
    width: 100,
    sortable: false,
    align: 'right',
    headerAlign: 'center',
    valueGetter: (value: number) => `¥${value}`,
  },
  {
    field: 'createTime',
    headerName: '申请时间',
    width: 170,
    sortable: false,
    headerAlign: 'center',
    valueGetter: (value: string) => formatTime(value),
  },
  {
    field: 'issuedAt',
    headerName: '开票时间',
    width: 170,
    sortable: false,
    headerAlign: 'center',
    valueGetter: (value: string | null) => formatTime(value),
  },
  {
    field: 'status',
    headerName: '状态',
    width: 100,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: ({ row }) => {
      const st = INVOICE_STATUS_CHIP[row.status]
      return <Chip label={st.label} color={st.color} size="small" />
    },
  },
]

export default function InvoiceRequestsPage() {
  const { currentSite } = useRole()
  const [status, setStatus] = useState<InvoiceStatus | ''>('')
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null)

  const [edges, setEdges] = useState<InvoiceRequestsQuery['invoiceRequests']['edges']>(
    [],
  )
  const [pageInfo, setPageInfo] = useState<
    InvoiceRequestsQuery['invoiceRequests']['pageInfo']
  >({ __typename: 'PageInfo', hasNextPage: false, endCursor: null })

  const [execute, { loading, error, fetchMore }] = useLazyQuery(
    InvoiceRequestsDocument,
    { fetchPolicy: 'network-only' },
  )

  const executeRef = useRef(execute)
  executeRef.current = execute

  const loadingMore = useRef(false)
  const lastFetchedCursor = useRef<string | null | undefined>(undefined)

  // (Re)load from the first page whenever the site or status filter changes
  useEffect(() => {
    if (!currentSite) return
    loadingMore.current = false
    lastFetchedCursor.current = undefined
    setEdges([])
    setPageInfo({ __typename: 'PageInfo', hasNextPage: false, endCursor: null })
    executeRef
      .current({
        variables: {
          filter: { siteId: currentSite.id, status: status || undefined },
        },
      })
      .then((result) => {
        if (result.data) {
          setEdges(result.data.invoiceRequests.edges)
          setPageInfo(result.data.invoiceRequests.pageInfo)
        }
      })
  }, [currentSite, status])

  const handleRowsScrollEnd = useCallback(() => {
    if (
      pageInfo.hasNextPage &&
      !loadingMore.current &&
      pageInfo.endCursor !== lastFetchedCursor.current
    ) {
      loadingMore.current = true
      lastFetchedCursor.current = pageInfo.endCursor
      fetchMore({
        variables: { after: pageInfo.endCursor },
      }).then((result) => {
        const more = result.data
        if (more) {
          setEdges((prev) => {
            const existingIds = new Set(prev.map((e) => e.node.id))
            const newEdges = more.invoiceRequests.edges.filter(
              (e) => !existingIds.has(e.node.id),
            )
            return [...prev, ...newEdges]
          })
          setPageInfo(more.invoiceRequests.pageInfo)
        }
        loadingMore.current = false
      })
    }
  }, [pageInfo, fetchMore])

  const rows = edges.map((e) => e.node)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h4">发票管理</Typography>
        <Box sx={{ flex: 1 }} />
        <TextField
          select
          label="状态"
          size="small"
          value={status}
          onChange={(e) => setStatus(e.target.value as InvoiceStatus | '')}
          sx={{ width: 140 }}
        >
          <MenuItem value="">全部</MenuItem>
          {Object.values(InvoiceStatus).map((s) => (
            <MenuItem key={s} value={s}>
              {INVOICE_STATUS_CHIP[s].label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {error && <Alert severity="error">加载发票申请失败: {error.message}</Alert>}

      <DataGridPro
        rows={rows}
        columns={columns}
        getRowId={(row: InvoiceRequestRow) => row.id}
        loading={loading}
        hideFooter
        disableRowSelectionOnClick
        disableColumnMenu
        density="compact"
        getRowHeight={() => 'auto'}
        onRowsScrollEnd={handleRowsScrollEnd}
        onRowClick={(params: GridRowParams<InvoiceRequestRow>) =>
          params.row.id && setSelectedRequestId(params.row.id)
        }
        localeText={{ noRowsLabel: '暂无发票申请' }}
        sx={{
          flex: 1,
          minHeight: 0,
          '& .MuiDataGrid-row': { cursor: 'pointer' },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            py: 1,
          },
        }}
      />

      <InvoiceRequestDialog
        invoiceRequestId={selectedRequestId}
        onClose={() => setSelectedRequestId(null)}
      />
    </Box>
  )
}
